import { InlineError } from "@/components/ui/InlineError";
import { Spinner } from "@/components/ui/Spinner";
import { Tabs } from "@/components/ui/Tabs";
import { getDepartment, isDepartmentId } from "@/grid/config/registry";
import {
  monthStart,
  nowToronto,
  quarterStart,
  toIsoDate,
  weekStartMonday,
  weekStartsOverlappingQuarter,
} from "@/grid/core/periods";
import { PeriodNavigator } from "@/targets/components/PeriodNavigator";
import { TargetInferencePanel } from "@/targets/components/TargetInferencePanel";
import { TargetSpreadsheet } from "@/targets/components/TargetSpreadsheet";
import { useTargetEditorData } from "@/targets/hooks/useTargetEditorData";
import {
  formatMonthLabel,
  formatQuarterLabel,
  formatWeekRange,
} from "@/targets/lib/target-period-labels";
import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

type PeriodTab = "tracking" | "weekly" | "monthly" | "quarterly";

const PERIOD_TABS: Array<{ id: PeriodTab; label: string }> = [
  { id: "tracking", label: "Targets tracking" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
];

function EmptyPeriodState({ message }: { message: string }) {
  return (
    <p className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
      {message}
    </p>
  );
}

export function TargetEditorPage() {
  const { departmentId = "sales" } = useParams<{ departmentId: string }>();
  const today = useMemo(() => nowToronto(), []);
  const anchorDate = toIsoDate(today);
  const [activeTab, setActiveTab] = useState<PeriodTab>("tracking");
  const [weekFocus, setWeekFocus] = useState(() => weekStartMonday(today));
  const [monthFocus, setMonthFocus] = useState(() => monthStart(today));
  const [quarterFocus, setQuarterFocus] = useState(() => quarterStart(today));

  const weeksInQuarter = useMemo(() => weekStartsOverlappingQuarter(today), [today]);

  const { data, isLoading, isError, refetch } = useTargetEditorData(departmentId, anchorDate, {
    weekFocusStart: weekFocus,
    quarterFocusStart: quarterFocus,
    monthScope: "year",
  });

  if (!isDepartmentId(departmentId)) {
    return <Navigate to="/targets/sales" replace />;
  }

  const department = getDepartment(departmentId);
  const currentWeek = weekStartMonday(today);
  const currentMonth = monthStart(today);
  const currentQuarter = quarterStart(today);

  const selectedMonthSheet = data?.sheets.monthlyQuarters.find(
    (entry) => entry.monthStart === monthFocus,
  );

  const weekOverridePicker = (
    <PeriodNavigator
      periods={weeksInQuarter}
      selected={weekFocus}
      current={currentWeek}
      onChange={setWeekFocus}
      formatOption={formatWeekRange}
      selectAriaLabel="Select week"
    />
  );

  const monthPicker = (
    <PeriodNavigator
      periods={data?.monthStarts ?? []}
      selected={monthFocus}
      current={currentMonth}
      onChange={setMonthFocus}
      formatOption={formatMonthLabel}
      selectAriaLabel="Select month"
    />
  );

  const quarterPicker = (
    <PeriodNavigator
      periods={data?.yearQuarterStarts ?? []}
      selected={quarterFocus}
      current={currentQuarter}
      onChange={setQuarterFocus}
      formatOption={formatQuarterLabel}
      selectAriaLabel="Select quarter"
    />
  );

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-white p-5 shadow-sm sm:p-6">
      <header className="mb-6 space-y-1">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--color-text)]">
          {department?.name} targets
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">
          Set weekly, monthly, and quarterly targets by team.
        </p>
      </header>

      <div className="mb-6">
        <Tabs
          aria-label="Target period"
          items={PERIOD_TABS.map((tab) => ({ value: tab.id, label: tab.label }))}
          value={activeTab}
          onChange={(value) => setActiveTab(value as PeriodTab)}
        />
      </div>

      {isLoading ? (
        <div className="flex min-h-40 items-center justify-center">
          <Spinner />
        </div>
      ) : null}

      {isError ? <InlineError message="Failed to load targets." /> : null}

      {data ? (
        <div className="space-y-6">
          {activeTab === "tracking" ? (
            <TargetInferencePanel
              departmentId={departmentId}
              metrics={data.department.metrics}
              configs={data.configs}
              onSaved={() => void refetch()}
              embedded
            />
          ) : null}

          {activeTab === "weekly" ? (
            <div className="space-y-6">
              {data.weeklyMetrics.length > 0 && data.sheets.weekly ? (
                <>
                  <TargetSpreadsheet
                    departmentId={departmentId}
                    periodType="week"
                    periodStart={weekFocus}
                    metrics={data.weeklyMetrics}
                    teams={data.teams}
                    sheet={data.sheets.weekly}
                    weekSectionFilter="recurring"
                    minimal
                    embedded
                    onSaved={() => void refetch()}
                  />

                  <TargetSpreadsheet
                    departmentId={departmentId}
                    periodType="week"
                    periodStart={weekFocus}
                    metrics={data.weeklyMetrics}
                    teams={data.teams}
                    sheet={data.sheets.weekly}
                    weekSectionFilter="override"
                    headerToolbar={weekOverridePicker}
                    minimal
                    embedded
                    onSaved={() => void refetch()}
                  />
                </>
              ) : (
                <EmptyPeriodState message="No weekly metrics for this department." />
              )}
            </div>
          ) : null}

          {activeTab === "monthly" ? (
            data.monthlyMetrics.length > 0 && data.monthStarts.length > 0 && selectedMonthSheet ? (
              <TargetSpreadsheet
                departmentId={departmentId}
                periodType="month"
                periodStart={selectedMonthSheet.monthStart}
                metrics={data.monthlyMetrics}
                teams={data.teams}
                sheet={selectedMonthSheet.sheet}
                headerToolbar={monthPicker}
                minimal
                embedded
                onSaved={() => void refetch()}
              />
            ) : (
              <EmptyPeriodState message="No monthly metrics for this department." />
            )
          ) : null}

          {activeTab === "quarterly" ? (
            data.quarterlyMetrics.length > 0 ? (
              data.sheets.quarterly ? (
                <TargetSpreadsheet
                  departmentId={departmentId}
                  periodType="quarter"
                  periodStart={quarterFocus}
                  metrics={data.quarterlyMetrics}
                  teams={data.teams}
                  sheet={data.sheets.quarterly}
                  headerToolbar={quarterPicker}
                  minimal
                  embedded
                  onSaved={() => void refetch()}
                />
              ) : (
                <EmptyPeriodState message="No explicit quarter targets for this quarter yet." />
              )
            ) : (
              <EmptyPeriodState message="Quarter targets are inferred from roll-up rules." />
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
